/**
 * elfbar.de DTC custom event tracking v3.3.0
 * Theme -> Shopify.analytics.publish('dtc:<event>', payload)
 * Standard GA4 ecommerce events remain owned by Shopify / Google & YouTube App.
 */
(() => {
  'use strict';

  const VERSION = '3.3.0';
  const EVENTS = new Set([
    'section_view',
    'section_interact',
    'carousel_slide_view',
    'carousel_slide_click',
    'faq_toggle',
    'nav_click',
    'search_open',
    'search_suggestion_click',
    'search_result_loaded',
    'product_card_click',
    'pagination_click',
    'collection_filter_apply',
    'collection_filter_clear',
    'collection_sort_change',
    'collection_tab_view',
    'collection_tab_click',
    'pdp_variant_change',
    'pdp_media_interact',
    'free_shipping_progress_view',
    'cart_quantity_change',
    'cart_remove_item',
    'cart_continue_shopping_click',
    'cart_checkout_click',
    'atc_banner_view',
    'atc_banner_interact',
    'article_progress',
    'article_complete',
    'article_cta_click',
    'newsletter_signup',
    'contact_form_submit',
    'locale_change',
    'app_widget_view',
    'app_widget_interact',
  ]);
  const BLOCKED_KEYS = new Set([
    'email',
    'phone',
    'telephone',
    'first_name',
    'last_name',
    'full_name',
    'name',
    'address',
    'postcode',
    'postal_code',
    'birthday',
    'birthdate',
    'customer_id',
    'order_note',
    'form_text',
    'form_value',
  ]);
  const body = document.body;
  const pending = [];
  const once = new Set();
  const carouselRoots = new WeakSet();
  const appRoots = new WeakSet();
  const atcBanners = new WeakSet();
  const tabSections = new WeakSet();
  const debugParams = new URLSearchParams(window.location.search);
  const debug = debugParams.has('dtc_debug')
    || debugParams.has('preview_theme_id')
    || Boolean(window.Shopify && window.Shopify.designMode);
  let flushTimer = 0;
  let lastSearchOpenAt = 0;
  let lastVariantKey = '';
  let lastVariantAt = 0;
  let pendingAdd = null;
  let cartCache = null;
  let cartCacheAt = 0;

  function clean(value, max = 120) {
    return String(value == null ? '' : value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max);
  }

  function number(value, fallback) {
    if (value == null || value === '') return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function eventId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'dtc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 12);
  }

  function pagePath(url) {
    try {
      const parsed = new URL(url || window.location.href, window.location.origin);
      return parsed.pathname.slice(0, 200);
    } catch (_) {
      return '';
    }
  }

  function context() {
    const data = body.dataset;
    const result = {
      site_id: data.dtcSiteId || 'elfbar_de',
      brand: data.dtcBrand || 'elfbar',
      market: data.dtcMarket || 'DE',
      tracking_version: VERSION,
      page_type: data.dtcPageType || 'unknown',
      page_template: data.dtcPageTemplate || 'unknown',
      shop_locale: data.dtcLocale || document.documentElement.lang || 'de',
      currency: data.dtcCurrency || 'EUR',
      customer_logged_in: data.dtcCustomerLoggedIn === 'true',
      debug_mode: debug,
      page_path: pagePath(),
    };
    if (data.dtcCollectionHandle) result.collection_handle = data.dtcCollectionHandle;
    if (data.dtcProductHandle) result.product_handle = data.dtcProductHandle;
    if (data.dtcArticleSlug) result.article_slug = data.dtcArticleSlug;
    return result;
  }

  function sanitize(params) {
    const result = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (BLOCKED_KEYS.has(key) || value == null || value === '') return;
      if (typeof value === 'string') {
        const max = key === 'page_path' || key.endsWith('_href') ? 200 : 120;
        const safe = clean(value, max);
        if (safe) result[key] = safe;
      } else if (typeof value === 'boolean') {
        result[key] = value;
      } else if (typeof value === 'number' && Number.isFinite(value)) {
        result[key] = value;
      }
    });
    return result;
  }

  function send(item) {
    if (!window.Shopify || !Shopify.analytics || typeof Shopify.analytics.publish !== 'function') {
      return false;
    }
    try {
      Shopify.analytics.publish('dtc:' + item.name, item.payload);
      return true;
    } catch (_) {
      return false;
    }
  }

  function flush() {
    while (pending.length && send(pending[0])) pending.shift();
  }

  function scheduleFlush() {
    if (flushTimer) return;
    let attempts = 0;
    flushTimer = window.setInterval(() => {
      attempts += 1;
      flush();
      if (!pending.length || attempts >= 60) {
        window.clearInterval(flushTimer);
        flushTimer = 0;
      }
    }, 500);
  }

  function publish(name, params = {}) {
    if (!EVENTS.has(name)) {
      if (debug && window.console) console.warn('[DTC] blocked event ' + clean(name, 80));
      return false;
    }
    const payload = sanitize({ ...context(), ...params, event_id: eventId() });
    const item = { name, payload };
    if (!send(item)) {
      if (pending.length >= 50) pending.shift();
      pending.push(item);
      scheduleFlush();
    }
    document.dispatchEvent(new CustomEvent('dtc:published', { detail: { name, payload } }));
    if (debug) {
      const debugEvents = Array.isArray(window.__DTC_DEBUG_EVENTS__) ? window.__DTC_DEBUG_EVENTS__ : [];
      debugEvents.push({ name, payload });
      if (debugEvents.length > 100) debugEvents.splice(0, debugEvents.length - 100);
      window.__DTC_DEBUG_EVENTS__ = debugEvents;
    }
    if (debug && window.console) console.info('[DTC] ' + name + ' ' + JSON.stringify(payload));
    return true;
  }

  window.DtcTracking = Object.freeze({
    version: VERSION,
    publish,
    getContext: context,
    events: Object.freeze(Array.from(EVENTS)),
  });

  function element(value) {
    if (value instanceof Element) return value;
    return value && value.parentElement ? value.parentElement : null;
  }

  function isVisible(el) {
    if (!el || !el.getClientRects().length || el.matches('[hidden], [aria-hidden="true"]')) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && number(style.opacity, 1) !== 0;
  }

  function visibleRatio(el) {
    if (!isVisible(el)) return 0;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;
    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const adjustedArea = Math.min(rect.width, window.innerWidth) * Math.min(rect.height, window.innerHeight);
    return adjustedArea ? (visibleWidth * visibleHeight) / adjustedArea : 0;
  }

  function linkLabel(target) {
    if (!target) return '';
    return clean(
      target.getAttribute('aria-label')
      || target.getAttribute('title')
      || target.querySelector?.('h1, h2, h3, h4, [data-atc-label]')?.textContent
      || target.textContent,
      80,
    );
  }

  function parseMoney(value) {
    if (!value) return null;
    let normalized = String(value).replace(/[^\d,.-]/g, '');
    if (!normalized) return null;
    if (normalized.includes(',') && normalized.includes('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else if (normalized.includes(',')) {
      normalized = normalized.replace(',', '.');
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : null;
  }

  function productHandle(url) {
    try {
      const match = new URL(url, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]).slice(0, 120) : '';
    } catch (_) {
      return '';
    }
  }

  function collectionHandle(url) {
    try {
      const match = new URL(url, window.location.origin).pathname.match(/\/collections\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]).slice(0, 120) : '';
    } catch (_) {
      return '';
    }
  }

  function targetType(url) {
    if (!url) return 'none';
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin !== window.location.origin) return 'external';
      if (parsed.pathname.includes('/products/')) return 'product';
      if (parsed.pathname.includes('/collections/')) return 'collection';
      if (parsed.pathname.includes('/blogs/')) return 'article';
      if (parsed.pathname.includes('/search')) return 'search';
      if (parsed.pathname === '/cart' || parsed.pathname.includes('/checkout')) return 'cart';
      if (/\.(pdf|zip|docx?|xlsx?)$/i.test(parsed.pathname)) return 'download';
      if (parsed.hash && parsed.pathname === window.location.pathname) return 'anchor';
      return 'page';
    } catch (_) {
      return 'none';
    }
  }

  function sectionElement(el) {
    return el?.closest('[id^="shopify-section-"]') || null;
  }

  function sectionName(section) {
    if (!section) return 'unknown';
    const typed = section.querySelector(':scope > script[data-section-type], script[data-section-type]')?.dataset.sectionType;
    if (typed) return clean(typed.replace(/^static-|^dynamic-/, ''), 60);
    const className = Array.from(section.classList || []).find((name) => (
      name.startsWith('shopify-section--') || name.endsWith('--section') || name.endsWith('-section')
    ));
    if (className) return clean(className.replace(/^shopify-section--/, '').replace(/--section$|-section$/, ''), 60);
    const raw = (section.id || '').replace('shopify-section-', '');
    const inferred = raw.includes('__') ? raw.split('__').pop() : raw;
    return clean(inferred.replace(/_[a-zA-Z0-9]{5,}$/, ''), 60) || 'unknown';
  }

  function sectionInfo(el) {
    const section = sectionElement(el) || (el?.matches?.('[id^="shopify-section-"]') ? el : null);
    if (!section) {
      return {
        section_name: 'unknown',
        section_position: 0,
        section_scope: 'unknown',
        section_confidence: 'low',
      };
    }
    const peers = Array.from(document.querySelectorAll('main [id^="shopify-section-"]'));
    const name = sectionName(section);
    let scope = 'template';
    if (section.closest('header, [data-site-header]')) scope = 'header_group';
    else if (section.closest('footer, [data-site-footer]')) scope = 'footer_group';
    else if (section.querySelector('.shopify-app-block')) scope = 'app';
    return {
      section_name: name,
      section_variant: clean(Array.from(section.classList || []).filter((item) => item !== 'shopify-section').slice(0, 3).join(' '), 80),
      section_position: Math.max(0, peers.indexOf(section) + 1),
      section_scope: scope,
      section_confidence: name === 'unknown' ? 'low' : 'high',
    };
  }

  function meaningfulSection(section) {
    if (!isVisible(section) || section.closest('[data-templates]')) return false;
    const humanContent = section.querySelector('img, video, form, a[href], button, h1, h2, h3, p');
    return Boolean(humanContent && (clean(section.textContent, 20) || section.querySelector('img, video')));
  }

  const sectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const section = entry.target;
        const key = 'section:' + (section.id || sectionName(section));
        if (!once.has(key) && visibleRatio(section) >= 0.5 && meaningfulSection(section)) {
          once.add(key);
          publish('section_view', sectionInfo(section));
          sectionObserver.unobserve(section);
        }
      });
    }, { rootMargin: '-25% 0px -25% 0px', threshold: [0] })
    : null;

  function observeSections(root = document) {
    const sections = Array.from(root.querySelectorAll?.('[id^="shopify-section-"]') || []);
    if (root instanceof Element && root.matches('[id^="shopify-section-"]')) sections.unshift(root);
    sections.forEach((section) => {
      if (section.dataset.dtcSectionObserved === '1' || section.closest('[data-templates]')) return;
      section.dataset.dtcSectionObserved = '1';
      if (sectionObserver) sectionObserver.observe(section);
      else if (meaningfulSection(section)) publish('section_view', sectionInfo(section));
    });
  }

  function carouselName(root) {
    return clean(sectionName(sectionElement(root)) || 'slideshow', 60);
  }

  function carouselSlides(root) {
    return Array.from(root.querySelectorAll('[data-slideshow-slide], .slideshow-slide'));
  }

  function slideInfo(root, slide) {
    const slides = carouselSlides(root);
    const link = slide.querySelector('a[href]');
    const actions = Array.from(slide.querySelectorAll('a[href], button')).filter(isVisible);
    return {
      ...sectionInfo(root),
      carousel_name: carouselName(root),
      carousel_position: sectionInfo(root).section_position,
      slide_index: Math.max(1, slides.indexOf(slide) + 1),
      slide_total: slides.length,
      slide_label: clean(slide.querySelector('.slideshow-slide__preheading')?.textContent, 80),
      slide_title: clean(slide.querySelector('.slideshow-slide__heading')?.textContent || linkLabel(link || slide), 120),
      slide_primary_cta: actions[0] ? linkLabel(actions[0]) : '',
      slide_secondary_cta: actions[1] ? linkLabel(actions[1]) : '',
      slide_has_link: Boolean(link),
      slide_href: link ? pagePath(link.href) : '',
      slide_target_type: link ? targetType(link.href) : 'none',
    };
  }

  function visibleSlides(root) {
    const slides = carouselSlides(root);
    const selected = slides.filter((slide) => (
      slide.classList.contains('is-selected')
      || slide.getAttribute('aria-hidden') === 'false'
      || slide.dataset.selected === 'true'
    ));
    if (selected.length) return selected;
    return slides.filter((slide) => visibleRatio(slide) >= 0.5);
  }

  function trackCarouselViews(root) {
    if (visibleRatio(root) < 0.25) return;
    visibleSlides(root).forEach((slide) => {
      const info = slideInfo(root, slide);
      const key = 'slide:' + (sectionElement(root)?.id || carouselName(root)) + ':' + info.slide_index;
      if (once.has(key)) return;
      once.add(key);
      publish('carousel_slide_view', info);
    });
  }

  function observeCarousel(root) {
    if (carouselRoots.has(root)) return;
    carouselRoots.add(root);
    const run = () => window.requestAnimationFrame(() => trackCarouselViews(root));
    new MutationObserver(run).observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'data-selected'],
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) run();
      }, { threshold: [0.25] }).observe(root);
    } else run();
  }

  function observeCarousels(root = document) {
    const roots = Array.from(root.querySelectorAll?.('[data-slideshow]') || []);
    if (root instanceof Element && root.matches('[data-slideshow]')) roots.unshift(root);
    roots.forEach((item) => {
      if (carouselSlides(item).length) observeCarousel(item);
    });
  }

  function cleanSearchTerm(raw) {
    const value = clean(raw, 80).toLocaleLowerCase('de-DE');
    const looksLikeEmail = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);
    const digits = value.replace(/\D/g, '');
    const looksLikePhone = digits.length >= 7 && /^[+\d\s()./-]+$/.test(value);
    return {
      value: looksLikeEmail || looksLikePhone ? '' : value,
      redacted: looksLikeEmail || looksLikePhone,
      length: value.length,
    };
  }

  function trackSearchOpen(openMethod, triggerLabel) {
    const now = Date.now();
    if (now - lastSearchOpenAt < 250) return;
    lastSearchOpenAt = now;
    publish('search_open', {
      search_surface: 'header_live_search',
      open_method: openMethod,
      trigger_label: clean(triggerLabel, 50),
      search_flydown_available: Boolean(document.querySelector('[data-live-search-flydown]')),
    });
  }

  function searchSuggestionInfo(suggestion, interactionMethod) {
    const flydown = suggestion.closest('[data-live-search-flydown]');
    const input = document.querySelector('[data-live-search-input]');
    const products = Array.from(flydown?.querySelectorAll('.search-flydown--product, [data-live-search-results] a[href*="/products/"]') || []);
    const allSuggestions = Array.from(flydown?.querySelectorAll('a[href], button') || []).filter(isVisible);
    const search = cleanSearchTerm(input?.value || suggestion.textContent || '');
    let type = 'other';
    if (suggestion.matches('.search-flydown--product, [href*="/products/"]')) type = 'product';
    else if (suggestion.matches('[href*="/collections/"]')) type = 'collection';
    else if (suggestion.matches('[href*="/blogs/"]')) type = 'article';
    else if (suggestion.matches('[href*="/pages/"]')) type = 'page';
    else if (suggestion.matches('[href*="/search"]')) type = 'view_all';
    else if (suggestion.closest('[data-live-search-quick-links]')) type = 'query';
    return {
      search_surface: 'header_live_search',
      suggestion_type: type,
      suggestion_position: Math.max(1, (type === 'product' ? products : allSuggestions).indexOf(suggestion) + 1),
      suggestion_label: search.redacted ? '' : linkLabel(suggestion),
      interaction_method: interactionMethod,
      search_term: search.value,
      search_term_redacted: search.redacted,
      search_term_length: search.length,
      result_count: products.length,
      product_handle: type === 'product' ? productHandle(suggestion.href) : '',
      target_href: suggestion.href ? pagePath(suggestion.href) : '',
    };
  }

  function trackSearchResults() {
    const isSearch = context().page_type === 'search' || window.location.pathname.replace(/\/+$/, '') === '/search';
    if (!isSearch) return;
    const search = cleanSearchTerm(new URLSearchParams(window.location.search).get('q') || '');
    const cards = document.querySelectorAll('[data-productgrid-items] [data-product-item], .productgrid--items [data-product-item]');
    const count = cards.length;
    const key = 'search:' + search.value + ':' + new URLSearchParams(window.location.search).get('page');
    if (once.has(key)) return;
    once.add(key);
    publish('search_result_loaded', {
      search_term: search.value,
      search_term_redacted: search.redacted,
      search_term_length: search.length,
      result_count: count,
      has_results: count > 0,
      page_number: number(new URLSearchParams(window.location.search).get('page'), 1),
      search_type: 'mixed',
      sort_by: new URLSearchParams(window.location.search).get('sort_by') || '',
    });
  }

  function productCardInfo(card, link, clickTarget) {
    const section = sectionElement(card);
    const candidates = Array.from(section?.querySelectorAll('[data-product-item], .productgrid--item') || []);
    const price = parseMoney(card.querySelector('.price__current, .productitem--price .money, [data-price]')?.textContent);
    const compareAt = parseMoney(card.querySelector('.price__compare-at, .productitem--sale-price, s.money')?.textContent);
    const badge = clean(card.querySelector('.productitem--badge, .product-badge, [data-badge]')?.textContent, 50);
    return {
      ...sectionInfo(card),
      product_handle: card.dataset.productHandle || productHandle(link.href),
      product_position: Math.max(1, candidates.indexOf(card) + 1),
      page_number: number(new URLSearchParams(window.location.search).get('page'), 1),
      click_target: clickTarget.closest('img, picture, [data-product-item-image]')
        ? 'image'
        : (clickTarget.closest('.productitem--title, h2, h3, h4') ? 'title' : 'card'),
      product_badge: badge,
      product_in_stock: !Boolean(card.querySelector('[data-quick-buy][disabled], .productitem--action-atc[disabled]')),
      product_has_discount: compareAt != null && price != null && compareAt > price,
      discount_percent: compareAt != null && price != null && compareAt > price
        ? Math.round((compareAt - price) * 100 / compareAt)
        : 0,
      source_section: sectionInfo(card).section_name,
      collection_handle: context().collection_handle,
    };
  }

  function normalizeSort(value) {
    return clean(value || 'unknown', 40).replace(/-/g, '_');
  }

  function sortDirection(value) {
    if (/ascending$/.test(value)) return 'ascending';
    if (/descending$/.test(value)) return 'descending';
    return 'none';
  }

  function slug(value) {
    return clean(value, 80)
      .toLocaleLowerCase('de-DE')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80) || 'unknown';
  }

  function tabItems(section) {
    return Array.from(section.querySelectorAll('.custom--collection-tab > div'));
  }

  function tabPanels(section) {
    const featured = section.querySelector('[data-featured-collection]');
    return Array.from(featured?.children || []).filter((item) => item.className.includes('custom-tab-'));
  }

  function activeTabIndex(section) {
    const items = tabItems(section);
    const active = items.findIndex((item) => Array.from(item.classList).some((name) => name.startsWith('custom-active-')));
    if (active >= 0) return active;
    const panels = tabPanels(section);
    return Math.max(0, panels.findIndex(isVisible));
  }

  function trackTabView(section, index, activationSource) {
    const items = tabItems(section);
    const panels = tabPanels(section);
    const item = items[index];
    const panel = panels[index];
    if (!item || !panel || !isVisible(panel)) return;
    const label = linkLabel(item);
    const id = slug(label);
    const key = 'tab:' + (sectionElement(section)?.id || section.className) + ':' + id;
    if (once.has(key)) return;
    once.add(key);
    publish('collection_tab_view', {
      ...sectionInfo(section),
      tab_id: id,
      tab_label: label,
      tab_position: index + 1,
      source_section: 'custom_tab_collection_list',
      product_count: panel.querySelectorAll('[data-product-item], .productgrid--item').length,
      activation_source: activationSource,
    });
  }

  function observeTabSection(section) {
    if (tabSections.has(section)) return;
    tabSections.add(section);
    const run = () => trackTabView(section, activeTabIndex(section), 'initial');
    new MutationObserver(() => trackTabView(section, activeTabIndex(section), 'user_click')).observe(section, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'aria-selected'],
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) run();
      }, { threshold: [0.25] }).observe(section);
    } else run();
  }

  function observeCollectionTabs(root = document) {
    const sections = Array.from(root.querySelectorAll?.('.collection-list__container') || [])
      .filter((item) => item.querySelector('.custom--collection-tab'));
    if (root instanceof Element && root.matches('.collection-list__container') && root.querySelector('.custom--collection-tab')) sections.unshift(root);
    sections.forEach(observeTabSection);
  }

  function optionName(control) {
    const group = control.closest('fieldset, .options-selection__select-wrapper')
      || control.closest('[data-variant-option]')
      || control.closest('[data-options-selection]');
    const nameEl = group?.querySelector('[data-variant-option-name]');
    const raw = nameEl?.getAttribute('data-variant-option-name') || nameEl?.textContent || control.name || 'option';
    return clean(raw.split(':')[0], 50);
  }

  function optionValue(control) {
    if (control.matches('select')) return clean(control.value, 100);
    if (control.matches('input[type="radio"]')) return clean(control.value, 100);
    return clean(control.dataset.variantOptionChosenValue || control.value, 100);
  }

  function trackVariantChange(control, previous) {
    window.setTimeout(() => {
      const selection = control.closest('[data-variant-selection]');
      const variantInput = selection?.querySelector('[data-variants], [name="id"]');
      const value = optionValue(control);
      const variantId = clean(variantInput?.value || selection?.getAttribute('variant'), 80);
      const key = optionName(control) + ':' + value + ':' + variantId;
      if (key === lastVariantKey && Date.now() - lastVariantAt < 1000) return;
      lastVariantKey = key;
      lastVariantAt = Date.now();
      const selected = variantInput?.selectedOptions?.[0];
      publish('pdp_variant_change', {
        product_handle: context().product_handle || productHandle(window.location.href),
        product_name: clean(document.querySelector('.product-title, [data-product-title], h1')?.textContent, 120),
        option_name: optionName(control),
        option_previous_value: clean(previous, 100),
        option_value: value,
        option_position: number(control.dataset.variantOptionIndex, 0) + 1,
        variant_id: variantId,
        variant_title: clean(selected?.textContent, 120),
        variant_available: !Boolean(selected?.disabled || document.querySelector('[data-product-atc][disabled]')),
        variant_price: parseMoney(document.querySelector('[data-price], .product-pricing .money')?.textContent),
      });
    }, 50);
  }

  function cartSurface(el) {
    if (el?.closest('[data-atc-banner]')) return 'atc_banner';
    if (el?.closest('[data-cart-sidebar]')) return 'cart_sidebar';
    if (el?.closest('[data-cart-drawer], .cart-drawer, .mini-cart')) return 'cart_drawer';
    return 'cart_page';
  }

  function cartItems() {
    return Array.from(document.querySelectorAll('[data-cartitem]'));
  }

  function cartItemInfo(item) {
    const link = item?.querySelector('a[href*="/products/"]');
    const input = item?.querySelector('[data-quantity-input]');
    return {
      cart_surface: cartSurface(item),
      product_handle: link ? productHandle(link.href) : '',
      variant_id: clean(item?.dataset.cartitemId, 80),
      product_title: clean(item?.querySelector('.cart-item--content-title')?.textContent, 120),
      product_quantity: number(input?.value, 0),
      line_position: Math.max(1, cartItems().indexOf(item) + 1),
      item_price: item?.dataset.cartitemPrice ? number(item.dataset.cartitemPrice, 0) / 100 : null,
    };
  }

  function cartTotal() {
    const visibleTotals = Array.from(document.querySelectorAll('[data-cart-total]')).filter(isVisible);
    return parseMoney(visibleTotals[0]?.textContent);
  }

  function cartItemCount() {
    return cartItems().reduce((total, item) => total + number(item.querySelector('[data-quantity-input]')?.value, 0), 0);
  }

  function cartState(force = false) {
    if (!force && cartCache && Date.now() - cartCacheAt < 1000) return Promise.resolve(cartCache);
    return window.fetch((window.Theme?.routes?.cart_url || '/cart') + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error('cart');
      return response.json();
    }).then((data) => {
      cartCache = data;
      cartCacheAt = Date.now();
      return data;
    }).catch(() => null);
  }

  function findCartItem(key) {
    return cartItems().find((item) => item.dataset.cartitemKey === key || item.dataset.cartitemId === key) || null;
  }

  function waitForQuantityChange(item, previous, method, attempt = 0) {
    const key = item.dataset.cartitemKey || item.dataset.cartitemId || '';
    window.setTimeout(() => {
      const currentItem = findCartItem(key) || item;
      const input = currentItem.querySelector('[data-quantity-input]');
      const current = number(input?.value, previous);
      if (current !== previous) {
        if (input) input.dataset.dtcQuantity = String(current);
        cartCacheAt = 0;
        const itemInfo = cartItemInfo(currentItem);
        const publishWhenCartMatches = (cartAttempt = 0) => {
          window.setTimeout(() => {
            cartCacheAt = 0;
            cartState(true).then((cart) => {
              const latestItem = cart?.items?.find((cartItem) => String(cartItem.variant_id) === itemInfo.variant_id);
              if (cart && latestItem && number(latestItem.quantity, current) !== current && cartAttempt < 5) {
                publishWhenCartMatches(cartAttempt + 1);
                return;
              }
              publish('cart_quantity_change', {
                ...itemInfo,
                product_quantity: current,
                previous_quantity: previous,
                new_quantity: current,
                quantity_delta: current - previous,
                change_type: current > previous ? 'increase' : 'decrease',
                change_method: method,
                cart_total_amount: cart ? number(cart.total_price, 0) / 100 : cartTotal(),
              });
            });
          }, cartAttempt ? 200 : 250);
        };
        publishWhenCartMatches();
      } else if (attempt < 8 && document.contains(currentItem)) {
        waitForQuantityChange(currentItem, previous, method, attempt + 1);
      }
    }, 250);
  }

  function initCartQuantities(root = document) {
    Array.from(root.querySelectorAll?.('[data-cartitem] [data-quantity-input]') || []).forEach((input) => {
      if (!input.dataset.dtcQuantity) input.dataset.dtcQuantity = String(number(input.value, 0));
    });
  }

  function freeShippingSurface(bar) {
    if (bar.closest('[data-atc-banner]')) return 'atc_banner';
    if (bar.classList.contains('free-shipping-bar--cart-sidebar')) return 'cart_sidebar';
    if (bar.classList.contains('free-shipping-bar--cart')) return 'cart_page';
    return cartSurface(bar);
  }

  function trackFreeShipping(bar) {
    if (!isVisible(bar) || visibleRatio(bar) < 0.25) return;
    cartState().then((cart) => {
      const threshold = number(body.dataset.dtcFreeShippingThreshold, 39);
      const total = cart ? number(cart.items_subtotal_price, 0) / 100 : cartTotal();
      if (total == null) return;
      const remaining = Math.max(0, Math.round((threshold - total) * 100) / 100);
      const state = remaining > 0 ? 'below_threshold' : 'reached';
      const surface = freeShippingSurface(bar);
      const key = 'shipping:' + surface + ':' + state + ':' + total;
      if (once.has(key)) return;
      try {
        const storageKey = 'dtc:v3.2:' + key;
        const previousAt = number(window.sessionStorage.getItem(storageKey), 0);
        if (Date.now() - previousAt < 5000) return;
        window.sessionStorage.setItem(storageKey, String(Date.now()));
      } catch (_) {
        // The in-memory key below still prevents duplicates when storage is unavailable.
      }
      once.add(key);
      publish('free_shipping_progress_view', {
        surface,
        shipping_state: state,
        status_text: clean(bar.querySelector('.free-shipping-bar__status')?.textContent, 100),
        threshold_amount: threshold,
        cart_total_amount: total,
        remaining_amount: remaining,
        progress_percent: threshold > 0 ? Math.min(100, Math.round(total * 100 / threshold)) : 100,
      });
    });
  }

  function observeFreeShipping(root = document) {
    const bars = Array.from(root.querySelectorAll?.('[data-free-shipping-bar]') || []);
    if (root instanceof Element && root.matches('[data-free-shipping-bar]')) bars.unshift(root);
    bars.forEach((bar) => {
      if (bar.dataset.dtcShippingObserved === '1') {
        trackFreeShipping(bar);
        return;
      }
      bar.dataset.dtcShippingObserved = '1';
      new MutationObserver(() => {
        cartCacheAt = 0;
        trackFreeShipping(bar);
      }).observe(bar, { childList: true, subtree: true, characterData: true });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) trackFreeShipping(bar);
        }, { threshold: [0.25] }).observe(bar);
      } else trackFreeShipping(bar);
    });
  }

  function addIntent(target) {
    const form = target?.closest('form[action*="/cart/add"], form[data-product-form], [data-product-form] form');
    if (!form) return;
    const card = form.closest('[data-product-item], .productgrid--item');
    const productLink = (card || form.closest('[id^="shopify-section-"]'))?.querySelector('a[href*="/products/"]');
    let source = 'other';
    if (card) source = 'product_card';
    else if (form.closest('.shopify-app-block')) source = 'app';
    else if (context().page_type === 'product') source = 'pdp';
    pendingAdd = {
      created_at: Date.now(),
      source_surface: source,
      product_handle: context().product_handle || productHandle(productLink?.href || window.location.href),
      variant_id: clean(form.querySelector('[name="id"]')?.value, 80),
      product_quantity: Math.max(1, number(form.querySelector('[name="quantity"]')?.value, 1)),
    };
  }

  function bannerInfo(banner) {
    const recent = pendingAdd && Date.now() - pendingAdd.created_at < 10000 ? pendingAdd : {};
    const cartTotalAmount = parseMoney(banner.querySelector('[data-atc-banner-cart-subtotal]')?.textContent);
    const shippingThreshold = number(body.dataset.dtcFreeShippingThreshold, 39);
    return {
      banner_surface: 'atc_banner',
      source_surface: recent.source_surface || 'unknown',
      product_handle: recent.product_handle || '',
      variant_id: recent.variant_id || '',
      product_quantity: recent.product_quantity || number(clean(banner.querySelector('[data-atc-banner-product-price-quantity]')?.textContent).split('×')[0], 1),
      display_mode: 'floating',
      item_price: parseMoney(banner.querySelector('[data-atc-banner-product-price-discounted]:not(.hidden), [data-atc-banner-product-price-value]')?.textContent),
      cart_item_count: number(banner.querySelector('[data-atc-banner-cart-button] span')?.textContent, null),
      cart_total_amount: cartTotalAmount,
      free_shipping_reached: cartTotalAmount != null && cartTotalAmount >= shippingThreshold,
    };
  }

  function observeAtcBanner(banner) {
    if (atcBanners.has(banner) || banner.closest('[data-templates]')) return;
    atcBanners.add(banner);
    const attempt = () => {
      if (!document.contains(banner) || !isVisible(banner)) return;
      publish('atc_banner_view', bannerInfo(banner));
      observeFreeShipping(banner);
    };
    window.setTimeout(attempt, 150);
  }

  function humanAppContent(root) {
    const candidates = Array.from(root.querySelectorAll('h1, h2, h3, p, a[href], button, input, select, iframe, img'));
    return candidates.some((item) => isVisible(item) && (item.matches('img, iframe, input, select') || clean(item.textContent, 10)));
  }

  function appSource(root) {
    const signature = (root.id + ' ' + root.className).toLocaleLowerCase('en-US');
    if (/judge[_-]?me|jdgm/.test(signature)) return 'judge_me';
    if (/hulk[_-]?form/.test(signature)) return 'hulk_form_builder';
    if (/unlimited[_-]?bundles|revy[_-]?bundle|rbu/.test(signature)) return 'unlimited_bundles';
    if (/easybundle|easy[_-]?bundle/.test(signature)) return 'eb_easy_bundle';
    if (/revenuehunt|quiz/.test(signature)) return 'revenuehunt_quiz';
    if (/kite|free[_-]?gift/.test(signature)) return 'kite_free_gift';
    if (/hoppy|trust[_-]?badge/.test(signature)) return 'hoppy_trust_badges';
    if (/transcy|translation|language/.test(signature)) return 'transcy';
    if (/ang[_-]?extra|mega[_-]?menu/.test(signature)) return 'ang_extra_menu';
    if (/widerruf|withdrawal/.test(signature)) return 'eu_widerruf';
    return '';
  }

  function widgetType(source) {
    return ({
      judge_me: 'reviews',
      hulk_form_builder: 'form',
      unlimited_bundles: 'bundle',
      eb_easy_bundle: 'bundle',
      revenuehunt_quiz: 'quiz',
      kite_free_gift: 'free_gift',
      hoppy_trust_badges: 'trust_badge',
      transcy: 'language',
      ang_extra_menu: 'menu',
      eu_widerruf: 'legal',
    })[source] || 'other';
  }

  function observeApp(root) {
    if (appRoots.has(root)) return;
    appRoots.add(root);
    const source = appSource(root);
    if (!source) return;
    const track = () => {
      if (!humanAppContent(root) || visibleRatio(root) < 0.5) return;
      const key = 'app:' + source + ':' + (root.id || widgetType(source));
      if (once.has(key)) return;
      once.add(key);
      const peers = Array.from(document.querySelectorAll('.shopify-app-block')).filter((item) => appSource(item));
      publish('app_widget_view', {
        ...sectionInfo(root),
        app_source: source,
        widget_type: widgetType(source),
        widget_position: Math.max(1, peers.indexOf(root) + 1),
        widget_id: clean(root.id, 80),
        source_section: sectionInfo(root).section_name,
      });
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) track();
      }, { threshold: [0.5] }).observe(root);
    } else track();
  }

  function observeApps(root = document) {
    const apps = Array.from(root.querySelectorAll?.('.shopify-app-block, [id^="shopify-block-"]') || []);
    if (root instanceof Element && root.matches('.shopify-app-block, [id^="shopify-block-"]')) apps.unshift(root);
    apps.forEach(observeApp);
  }

  function articleContainer() {
    return document.querySelector('#ai-blog-content.ai-blog-body, .article--content');
  }

  function articleCtaLink(target) {
    if (context().page_type !== 'article') return null;
    const link = target.closest(
      '#ai-blog-content.ai-blog-body a[href], .article--content a[href], '
      + '.ai-blog-sidebar .ai-blog-product-item a[href]',
    );
    if (!link) return null;
    if (link.closest('.toc, .ai-blog-outline, .ai-blog-breadcrumbs, .ai-blog-author-bio-yg, .ai-blog-meta, .ai-blog-article-tag')) {
      return null;
    }
    try {
      const url = new URL(link.href, window.location.origin);
      if (url.origin === window.location.origin
        && url.pathname === window.location.pathname
        && url.search === window.location.search
        && url.hash) return null;
    } catch (_) {
      return null;
    }
    return link;
  }

  function articleCtaLinks() {
    return Array.from(document.querySelectorAll(
      '#ai-blog-content.ai-blog-body a[href], .article--content a[href], '
      + '.ai-blog-sidebar .ai-blog-product-item a[href]',
    )).filter((link) => articleCtaLink(link) === link);
  }

  function articleData() {
    const container = articleContainer();
    return {
      article_title: clean(document.querySelector('.article--title, h1')?.textContent, 120),
      article_word_count: container ? clean(container.textContent, 200000).split(/\s+/).filter(Boolean).length : 0,
    };
  }

  function articlePercent() {
    const container = articleContainer();
    if (!container) return 0;
    const rect = container.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    const height = Math.max(1, rect.height);
    const read = Math.max(0, Math.min(height, window.scrollY + window.innerHeight - top));
    return Math.min(100, Math.round(read * 100 / height));
  }

  function initArticleProgress() {
    if (context().page_type !== 'article' || !articleContainer()) return;
    const started = Date.now();
    let ticking = false;
    const run = () => {
      ticking = false;
      const percent = articlePercent();
      [25, 50, 75, 100].forEach((threshold) => {
        const key = 'article-progress:' + threshold;
        if (percent >= threshold && !once.has(key)) {
          once.add(key);
          publish('article_progress', {
            ...articleData(),
            progress: String(threshold),
            scroll_percent: threshold,
            time_on_article: Math.round((Date.now() - started) / 1000),
          });
        }
      });
      if (percent >= 100 && Date.now() - started >= 10000 && !once.has('article-complete')) {
        once.add('article-complete');
        publish('article_complete', {
          ...articleData(),
          scroll_percent: 100,
          time_on_article: Math.round((Date.now() - started) / 1000),
          completion_method: 'scroll_and_time',
        });
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(run);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.setInterval(() => {
      if (articlePercent() >= 100) run();
    }, 1000);
    run();
  }

  function newsletterLocation(success) {
    if (success.closest('footer, [data-site-footer]')) return 'footer';
    if (success.closest('.pxs-newsletter-section, .newsletter-section')) return 'homepage_section';
    if (success.closest('.article--section')) return 'article';
    return 'other';
  }

  function trackNewsletterSuccess(root = document) {
    const successes = Array.from(root.querySelectorAll?.('.newsletter-success') || []);
    if (root instanceof Element && root.matches('.newsletter-success')) successes.unshift(root);
    successes.forEach((success) => {
      if (!isVisible(success)) return;
      const location = newsletterLocation(success);
      // Shopify exposes one page-level successful form state. Some templates
      // render that state in both the primary newsletter section and footer,
      // so location-scoped keys can double-count a single subscription.
      const key = 'newsletter:' + pagePath();
      try {
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, '1');
      } catch (_) {
        if (once.has(key)) return;
        once.add(key);
      }
      publish('newsletter_signup', {
        ...sectionInfo(success),
        signup_location: location,
        signup_method: 'shopify_form',
        success_state: 'confirmed',
      });
    });
  }

  function trackContactFormSuccess(root = document) {
    if (!document.querySelector('[data-template-contact]')) return;
    const selector = '.contact-message.message--success, [class*="ai-contact-form__success-"]';
    const successes = Array.from(root.querySelectorAll?.(selector) || []);
    if (root instanceof Element && root.matches(selector)) successes.unshift(root);
    successes.forEach((success) => {
      if (!isVisible(success)) return;
      const key = 'contact-form-submit:' + pagePath();
      try {
        if (window.sessionStorage.getItem(key)) return;
        window.sessionStorage.setItem(key, '1');
      } catch (_) {
        if (once.has(key)) return;
        once.add(key);
      }
      publish('contact_form_submit', {
        form_location: 'contact_page',
        form_type: 'shopify_contact',
        success_state: 'confirmed',
      });
    });
  }

  function filterEntries(url) {
    try {
      const parsed = new URL(url || window.location.href, window.location.origin);
      return Array.from(parsed.searchParams.entries()).filter(([key]) => key.startsWith('filter.'));
    } catch (_) {
      return [];
    }
  }

  function productResultCount() {
    return document.querySelectorAll(
      '[data-productgrid-items] [data-product-item], .productgrid--items [data-product-item]',
    ).length;
  }

  function filterGroup(link, parameterName) {
    const name = clean(link.dataset.name || parameterName, 120);
    if (name) {
      return slug(name
        .replace(/^filter\.[pvt]\./, '')
        .replace(/^(option|t\.shopify)\./, ''));
    }
    const title = link.closest('[data-filter-group]')?.querySelector('.collection-filters__filter-title');
    return slug(title?.textContent);
  }

  function filterValue(link, parameterValue) {
    return clean(
      link.dataset.value
        || parameterValue
        || link.querySelector('.active-filter-text, .collection-filters__filter-list-item-text')?.textContent
        || link.textContent,
      80,
    ).replace(/\s*\(\d+\)\s*$/, '').replace(/\s*Filter entfernen\s*$/i, '');
  }

  function trackFilter(link, clear) {
    const next = filterEntries(link.href);
    const current = filterEntries(window.location.href);
    const changed = clear
      ? current.find(([key, value]) => !next.some(([nextKey, nextValue]) => nextKey === key && nextValue === value))
      : next.find(([key, value]) => !current.some(([activeKey, activeValue]) => activeKey === key && activeValue === value));
    if (!clear) {
      if (!changed) return;
    }
    publish(clear ? 'collection_filter_clear' : 'collection_filter_apply', {
      list_type: context().page_type === 'search' ? 'search' : 'collection',
      filter_group: clear && link.matches('.filter-item-clear-all') ? 'all' : filterGroup(link, changed?.[0]),
      filter_value: clear && link.matches('.filter-item-clear-all') ? 'all' : filterValue(link, changed?.[1]),
      clear_scope: clear ? (link.matches('.filter-item-clear-all') ? 'all' : 'single') : '',
      active_filter_count: clear ? '' : next.length,
      active_filter_count_after: clear ? next.length : '',
      result_count: productResultCount(),
      collection_handle: context().collection_handle,
    });
  }

  function localeSurface(option) {
    if (option.matches('[data-custom-lang-option]')) return 'footer';
    if (option.closest('footer, [data-site-footer]')) return 'footer';
    if (option.closest('[class*="mobile"], [data-mobile-menu]')) return 'mobile';
    if (option.closest('header, [data-site-header], .utility-bar')) return 'header';
    return 'unknown';
  }

  function trackLocaleChange(option, event) {
    const previous = clean(document.documentElement.lang || body.dataset.dtcLocale || 'de', 20).toLocaleLowerCase('en-US');
    const locale = clean(option.dataset.value || option.getAttribute('lang'), 20).toLocaleLowerCase('en-US');
    if (!locale || locale === previous) return;
    publish('locale_change', {
      previous_locale: previous,
      locale,
      locale_surface: localeSurface(option),
      interaction_method: interactionMethod(event),
    });
  }

  function selectedMediaControl() {
    return document.querySelector('[data-gallery-thumbnail][data-gallery-selected="true"], [data-gallery-figure][data-gallery-selected="true"]');
  }

  function mediaData(control) {
    const selected = control?.matches('[data-gallery-expand], .pswp__button') ? selectedMediaControl() : control;
    const rawType = clean(selected?.dataset.mediaType, 40).toLocaleLowerCase('en-US');
    const mediaType = ['image', 'video', 'model', 'external_video'].includes(rawType) ? rawType : 'other';
    const index = number(selected?.dataset.galleryIndex, 0) + 1;
    return {
      media_type: mediaType,
      media_index: Math.max(1, index),
      media_total: document.querySelectorAll('[data-gallery-thumbnail]').length || document.querySelectorAll('[data-gallery-figure]').length,
      media_alt: clean(selected?.getAttribute('aria-label') || selected?.querySelector('img')?.getAttribute('alt'), 120),
    };
  }

  function trackPdpMedia(control, interactionType) {
    publish('pdp_media_interact', {
      ...mediaData(control),
      interaction_type: interactionType,
    });
  }

  function trackFaq(summary) {
    window.setTimeout(() => {
      const details = summary.closest('details');
      const customItem = summary.closest('.elfbar-faq-item');
      const scope = details?.parentElement || customItem?.parentElement;
      const items = Array.from(scope?.querySelectorAll('details, .elfbar-faq-item') || []);
      publish('faq_toggle', {
        ...sectionInfo(summary),
        faq_context: context().page_type,
        faq_question: clean(summary.querySelector('.faq__summary-heading, span')?.textContent || summary.textContent, 120),
        faq_index: Math.max(1, items.indexOf(details || customItem) + 1),
        faq_state: details ? (details.open ? 'open' : 'closed') : (customItem?.classList.contains('active') ? 'open' : 'closed'),
      });
    }, 0);
  }

  function interactionMethod(event) {
    return event.detail === 0 ? 'keyboard' : 'click';
  }

  document.addEventListener('focusin', (event) => {
    const target = element(event.target);
    if (target?.matches('[data-live-search-input]')) trackSearchOpen('input_focus', target.getAttribute('placeholder'));
  }, true);

  document.addEventListener('keydown', (event) => {
    const target = element(event.target);
    if (!target) return;
    if (event.key === 'Enter') {
      const suggestion = target.closest('[data-live-search-flydown] a[href], [data-live-search-flydown] button');
      if (suggestion) publish('search_suggestion_click', searchSuggestionInfo(suggestion, 'keyboard_enter'));
    }
  }, true);

  document.addEventListener('submit', (event) => {
    const target = element(event.target);
    if (target?.matches('form[action*="/cart/add"], form[data-product-form]')) addIntent(target);
  }, true);

  document.addEventListener('change', (event) => {
    const target = element(event.target);
    if (!target) return;

    const variant = target.closest('[data-variant-option], [data-variant-option-value]');
    if (variant && context().page_type === 'product') {
      const control = variant.matches('[data-variant-option]') ? variant : variant.closest('[data-variant-option]') || variant;
      const previous = control.dataset.variantOptionChosenValue || '';
      trackVariantChange(control.matches('input[type="radio"]') ? variant : control, previous);
      return;
    }

    const sort = target.closest('[data-productgrid-trigger-sort]');
    if (sort) {
      const previous = sort.dataset.dtcPreviousSort || new URLSearchParams(window.location.search).get('sort_by') || 'manual';
      const current = normalizeSort(sort.value);
      if (normalizeSort(previous) !== current) {
        sort.dataset.dtcPreviousSort = sort.value;
        publish('collection_sort_change', {
          previous_sort: normalizeSort(previous),
          sort_by: current,
          sort_direction: sortDirection(current),
          sort_surface: isVisible(sort) ? 'desktop' : 'mobile',
          result_count: document.querySelectorAll('[data-productgrid-items] [data-product-item], .productgrid--items [data-product-item]').length,
          collection_handle: context().collection_handle,
        });
      }
      return;
    }

    const cartInput = target.closest('[data-cartitem] [data-quantity-input]');
    if (cartInput) {
      const item = cartInput.closest('[data-cartitem]');
      const previous = number(cartInput.dataset.dtcQuantity, number(cartInput.defaultValue, number(cartInput.value, 0)));
      waitForQuantityChange(item, previous, 'quantity_input');
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = element(event.target);
    if (!target) return;

    const searchSubmit = target.closest('[data-live-search-submit]');
    if (searchSubmit && !document.activeElement?.matches('[data-live-search-input]')) {
      trackSearchOpen('search_icon', linkLabel(searchSubmit));
    }

    const suggestion = target.closest('[data-live-search-flydown] a[href], [data-live-search-flydown] button');
    if (suggestion) {
      publish('search_suggestion_click', searchSuggestionInfo(suggestion, 'click'));
      return;
    }

    const faq = target.closest('[data-faq-summary], .faq__summary, .elfbar-faq-question');
    if (faq) {
      trackFaq(faq);
      return;
    }

    const localeOption = target.closest('[data-disclosure-option][lang], [data-custom-lang-option]');
    if (localeOption) {
      trackLocaleChange(localeOption, event);
      return;
    }

    const clearFilter = target.closest(
      '.active-filter-group__item[href], .filter-item-clear-all[href], '
      + '.collection-filters__filter-link[data-filter-active][href]',
    );
    if (clearFilter && ['collection', 'search'].includes(context().page_type)) {
      trackFilter(clearFilter, true);
      return;
    }

    const applyFilter = target.closest('.collection-filters__filter-link:not([data-filter-active])[href]');
    if (applyFilter && ['collection', 'search'].includes(context().page_type)) {
      trackFilter(applyFilter, false);
      return;
    }

    const tabItem = target.closest('.custom--collection-tab > div');
    if (tabItem) {
      const section = tabItem.closest('.collection-list__container');
      const items = tabItems(section);
      const previousIndex = activeTabIndex(section);
      const index = items.indexOf(tabItem);
      if (index >= 0 && index !== previousIndex) {
        const label = linkLabel(tabItem);
        publish('collection_tab_click', {
          ...sectionInfo(tabItem),
          tab_id: slug(label),
          tab_label: label,
          tab_position: index + 1,
          previous_tab_id: slug(linkLabel(items[previousIndex])),
          interaction_method: interactionMethod(event),
          source_section: 'custom_tab_collection_list',
        });
        window.setTimeout(() => trackTabView(section, index, interactionMethod(event)), 0);
      }
      return;
    }

    const sortButton = target.closest('[data-productgrid-trigger-sort-button]');
    if (sortButton && !sortButton.disabled) {
      const container = sortButton.closest('[data-productgrid-sort-content]');
      const active = container?.querySelector('[data-productgrid-trigger-sort-button][disabled]');
      const current = normalizeSort(sortButton.value);
      publish('collection_sort_change', {
        previous_sort: normalizeSort(active?.value || new URLSearchParams(window.location.search).get('sort_by') || 'manual'),
        sort_by: current,
        sort_direction: sortDirection(current),
        sort_surface: 'modal',
        result_count: document.querySelectorAll('[data-productgrid-items] [data-product-item], .productgrid--items [data-product-item]').length,
        collection_handle: context().collection_handle,
      });
      return;
    }

    const pagination = target.closest('.pagination--container a[href], nav[aria-label="Pagination"] a[href]');
    if (pagination) {
      const url = new URL(pagination.href, window.location.origin);
      const label = linkLabel(pagination);
      const pageLinks = Array.from(pagination.closest('nav')?.querySelectorAll('a[href]') || []);
      publish('pagination_click', {
        ...sectionInfo(pagination),
        from_page: number(new URLSearchParams(window.location.search).get('page'), 1),
        to_page: number(url.searchParams.get('page'), 1),
        total_pages: Math.max(1, ...pageLinks.map((item) => {
          try {
            return number(new URL(item.href, window.location.origin).searchParams.get('page'), 1);
          } catch (_) {
            return 1;
          }
        })),
        click_type: /next|weiter|nächste|naechste|›|»|→/i.test(label)
          ? 'next'
          : (/prev|zurück|vorherige|‹|«|←/i.test(label) ? 'previous' : 'page_number'),
        list_type: context().page_type === 'search' ? 'search' : (context().page_type === 'article' ? 'blog' : 'collection'),
      });
      return;
    }

    const quantityButton = target.closest('[data-cartitem] [data-quantity-plus], [data-cartitem] [data-quantity-minus]');
    if (quantityButton) {
      const item = quantityButton.closest('[data-cartitem]');
      const input = item.querySelector('[data-quantity-input]');
      const previous = number(input?.dataset.dtcQuantity, number(input?.value, 0));
      waitForQuantityChange(item, previous, quantityButton.matches('[data-quantity-plus]') ? 'plus_button' : 'minus_button');
      return;
    }

    const remove = target.closest('[data-cartitem-remove]');
    if (remove) {
      const item = remove.closest('[data-cartitem]');
      publish('cart_remove_item', {
        ...cartItemInfo(item),
        removal_method: 'remove_button',
        cart_total_amount: cartTotal(),
      });
      return;
    }

    const checkout = target.closest('[name="checkout"], .atc-banner__form button[type="submit"]');
    if (checkout) {
      const checkoutTotal = parseMoney(checkout.closest('[data-atc-banner]')?.querySelector('[data-atc-banner-cart-subtotal]')?.textContent) || cartTotal();
      publish('cart_checkout_click', {
        cart_surface: cartSurface(checkout),
        cart_item_count: number(checkout.closest('[data-atc-banner]')?.querySelector('[data-atc-banner-cart-button] span')?.textContent, cartItemCount()),
        cart_total_amount: checkoutTotal,
        has_discount: Boolean(document.querySelector('.discount-list-item')),
        free_shipping_reached: checkoutTotal != null && checkoutTotal >= number(body.dataset.dtcFreeShippingThreshold, 39),
        button_label: linkLabel(checkout),
      });
      return;
    }

    const continueShopping = target.closest('.cart-continue, .cartitems-empty--inner a[href*="/collections"]');
    if (continueShopping) {
      publish('cart_continue_shopping_click', {
        cart_surface: cartSurface(continueShopping),
        destination_type: targetType(continueShopping.href),
        target_href: pagePath(continueShopping.href),
        cart_item_count: cartItemCount(),
        cart_total_amount: cartTotal(),
      });
      return;
    }

    const bannerControl = target.closest('[data-atc-banner] a[href], [data-atc-banner] button');
    if (bannerControl && !bannerControl.closest('[data-templates]')) {
      const banner = bannerControl.closest('[data-atc-banner]');
      let type = 'other';
      if (bannerControl.matches('[data-atc-banner-close]')) type = 'close';
      else if (bannerControl.matches('[data-atc-banner-cart-button]')) type = 'view_cart';
      else if (bannerControl.href && bannerControl.href.includes('/products/')) type = 'product_link';
      publish('atc_banner_interact', {
        ...bannerInfo(banner),
        interaction_type: type,
        target_href: bannerControl.href ? pagePath(bannerControl.href) : '',
        button_label: linkLabel(bannerControl),
      });
      return;
    }

    const addButton = target.closest('[data-product-atc], [data-quick-buy], .productitem--action-atc');
    if (addButton) addIntent(addButton);

    if (context().page_type === 'product') {
      const thumbnail = target.closest('[data-gallery-thumbnail]');
      const expand = target.closest('[data-gallery-expand], .product-gallery--expand');
      const close = target.closest('.pswp__button--close');
      const video = target.closest('[data-gallery-figure][data-media-type="video"] video, [data-gallery-figure][data-media-type="external_video"] button');
      const model = target.closest('[data-gallery-figure][data-media-type="model"] model-viewer, [data-shopify-xr]');
      if (thumbnail) {
        trackPdpMedia(thumbnail, 'thumbnail_select');
        return;
      }
      if (expand) {
        trackPdpMedia(expand, 'zoom_open');
        return;
      }
      if (close) {
        trackPdpMedia(close, 'zoom_close');
        return;
      }
      if (video) {
        trackPdpMedia(video.closest('[data-gallery-figure]'), 'video_play');
        return;
      }
      if (model) {
        trackPdpMedia(model.closest('[data-gallery-figure]') || model, 'model_open');
        return;
      }
    }

    const productLink = target.closest('a[data-product-page-link], [data-product-item] a[href*="/products/"], .productgrid--item a[href*="/products/"]');
    const productCard = productLink?.closest('[data-product-item], .productgrid--item');
    if (productLink && productCard && !target.closest('[data-quick-buy], .productitem--action-atc')) {
      publish('product_card_click', productCardInfo(productCard, productLink, target));
    }

    const carousel = target.closest('[data-slideshow]');
    if (carousel) {
      const slide = target.closest('[data-slideshow-slide], .slideshow-slide');
      if (slide) {
        const action = target.closest('a[href], button');
        const actions = Array.from(slide.querySelectorAll('a[href], button')).filter(isVisible);
        const actionIndex = action ? actions.indexOf(action) : -1;
        publish('carousel_slide_click', {
          ...slideInfo(carousel, slide),
          click_area: target.closest('img, picture')
            ? 'image'
            : (actionIndex === 0 ? 'primary_cta' : (actionIndex === 1 ? 'secondary_cta' : (target.closest('h1, h2, h3, h4') ? 'title' : 'background'))),
          click_label: linkLabel(action || target),
          click_href: action?.href ? pagePath(action.href) : '',
          click_target_type: action?.href ? targetType(action.href) : 'none',
        });
      }
      return;
    }

    const articleLink = articleCtaLink(target);
    if (articleLink) {
      const links = articleCtaLinks();
      publish('article_cta_click', {
        ...articleData(),
        target_type: targetType(articleLink.href),
        link_text: linkLabel(articleLink),
        link_zone: articleLink.closest('.ai-blog-sidebar .ai-blog-product-item, [class*="product"]') ? 'article_product_card' : 'article_body',
        link_position: Math.max(1, links.indexOf(articleLink) + 1),
        target_href: pagePath(articleLink.href),
        product_handle: productHandle(articleLink.href),
        scroll_percent: articlePercent(),
        is_attributable_to_article: true,
      });
      return;
    }

    const app = target.closest('.shopify-app-block, [id^="shopify-block-"]');
    const source = app ? appSource(app) : '';
    if (app && source) {
      const interactive = target.closest('a[href], button, input[type="button"], input[type="submit"], summary, [role="button"]');
      if (interactive && !interactive.closest('[data-product-item], [data-cartitem], form[action*="/cart/add"]')) {
        let type = 'click';
        if (interactive.matches('summary')) type = interactive.closest('details')?.open ? 'collapse' : 'expand';
        else if (/start|begin|quiz/i.test(linkLabel(interactive))) type = 'start';
        else if (/close|schließen/i.test(linkLabel(interactive))) type = 'close';
        publish('app_widget_interact', {
          ...sectionInfo(app),
          app_source: source,
          widget_type: widgetType(source),
          widget_id: clean(app.id, 80),
          interaction_type: type,
          interaction_target: linkLabel(interactive),
          source_section: sectionInfo(app).section_name,
          result_state: 'unknown',
        });
        return;
      }
    }

    const nav = target.closest('header a[href], [data-site-header] a[href], nav a[href], footer a[href], [data-site-footer] a[href]');
    if (nav && !nav.closest('[data-product-item], .productgrid--item, [data-live-search-flydown], [data-atc-banner]')
      && !nav.matches('[href="/"], [class*="logo"], [class*="cart"], [class*="search"]')) {
      let location = 'main_menu';
      if (nav.closest('footer, [data-site-footer]')) location = 'footer';
      else if (nav.closest('[class*="mobile"], [data-mobile-menu]')) location = 'mobile_menu';
      else if (nav.closest('[class*="mega"], [data-mega-menu]')) location = 'mega_menu';
      publish('nav_click', {
        nav_location: location,
        nav_item: linkLabel(nav),
        nav_parent: clean(nav.closest('li')?.parentElement?.closest('li')?.querySelector(':scope > a')?.textContent || 'root', 80),
        nav_level: Math.max(1, nav.closest('li')?.parentElement?.closest('li') ? 2 : 1),
        nav_position: Math.max(1, Array.from(nav.closest('nav, footer')?.querySelectorAll('a[href]') || []).indexOf(nav) + 1),
        nav_href: pagePath(nav.href),
        interaction_method: interactionMethod(event),
      });
      return;
    }

    const interactive = target.closest('a[href], button, [role="button"], input[type="submit"], summary');
    if (!interactive || !sectionElement(interactive)) return;
    if (interactive.closest(
      'header, footer, nav, [data-product-item], .productgrid--item, [data-slideshow], [data-faq], '
      + '.elfbar-faq-container, [data-live-search], [data-product-form], [data-cartitem], [data-atc-banner], '
      + '.newsletter, .article--content, #ai-blog-content, .ai-blog-sidebar .ai-blog-product-item, '
      + '.shopify-app-block, .custom--collection-tab, .pagination--container, .collection-filters__filter-link',
    )) return;
    publish('section_interact', {
      ...sectionInfo(interactive),
      interact_type: interactionMethod(event),
      interact_target: interactive.tagName.toLocaleLowerCase('en-US'),
      interact_label: linkLabel(interactive),
      target_href: interactive.href ? pagePath(interactive.href) : '',
    });
  }, true);

  function scanAdded(node) {
    if (!(node instanceof Element)) return;
    observeSections(node);
    observeCarousels(node);
    observeCollectionTabs(node);
    observeFreeShipping(node);
    observeApps(node);
    initCartQuantities(node);
    trackNewsletterSuccess(node);
    trackContactFormSuccess(node);
    if (node.matches('[data-atc-banner]')) observeAtcBanner(node);
    node.querySelectorAll('[data-atc-banner]').forEach(observeAtcBanner);
  }

  function boot() {
    observeSections();
    observeCarousels();
    observeCollectionTabs();
    observeFreeShipping();
    observeApps();
    initCartQuantities();
    trackSearchResults();
    trackNewsletterSuccess();
    trackContactFormSuccess();
    initArticleProgress();

    document.querySelectorAll('[data-productgrid-trigger-sort]').forEach((select) => {
      select.dataset.dtcPreviousSort = select.value;
    });

    new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(scanAdded));
    }).observe(body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => scanAdded(event.target));
})();
